import { useEffect, useRef, useState } from 'react';
import { componentIds, genericActionsIds } from 'utilities/constants';
import { ModalButtonProps } from 'components/modal/modal.interfaces';
import Modal from 'components/modal/modalComponent';
import { useGetComponent } from 'hooks/useGetComponent';
import InputComponent from 'components/inputComponent';
import { PrescribeMedicationModal } from 'components/PrescribeMedicationModal';
import { VIRTUAL_PHARMACIES } from 'utilities/constants';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import { createModal } from 'layout/createModal';
import { useModal } from 'layout/useModal';
import {
  useGetFhirProviderByRequestIdQuery,
  useLinkFullscriptProviderAccountMutation,
} from 'graphql/generated/remote-schema-hasura';
import Loader from 'components/loaderComponent';
import { FullscriptAuthLinkState } from '../../fullscript/fullscript.interface';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DASHBOARD,
  MY_PATIENTS,
  MY_PATIENTS_BIO_INFO,
  MY_PATIENTS_BIO_INFO_PATIENT_INFO,
} from '../../../utilities/routes';

const CODE_SEARCH_PARAM = 'code';
const STATE_SEARCH_PARAM = 'state';

export const VirtualPharmaciesModal = createModal<
  {
    appointmentId?: string;
    patientId: string;
    encounterId?: string;
    onModalClose?: () => void;
  },
  void
>('virtual-pharmacies-modal', ({ isOpen, state, close }) => {
  const [searchParams] = useSearchParams();
  const queryRun = useRef(false);
  const navigate = useNavigate();
  const [virtualPlatformSelect, setVirtualPlatformSelect] = useState<string>();
  const [openPrescriptionModal] = useModal(PrescribeMedicationModal);

  const onSuccess = async (state: FullscriptAuthLinkState) => {
    console.log('CDX-4336');
    await openPrescriptionModal({
      platform: VIRTUAL_PHARMACIES.fullscript,
      patientId: state.patientId,
      encounterId: state.appointmentId,
      onCloseCallback: state.onModalClose,
    });
    navigate(
      `${MY_PATIENTS}/${state.patientId}/${MY_PATIENTS_BIO_INFO}/${MY_PATIENTS_BIO_INFO_PATIENT_INFO}`,
    );
  };
  const onFailure = (state: Partial<FullscriptAuthLinkState>) => {
    if (state.patientId) {
      navigate(
        `${MY_PATIENTS}/${state.patientId}/${MY_PATIENTS_BIO_INFO}/${MY_PATIENTS_BIO_INFO_PATIENT_INFO}`,
      );
    } else {
      navigate(DASHBOARD);
    }
  };

  const [linkProvider] = useLinkFullscriptProviderAccountMutation({
    refetchQueries: ['GetFhirProviderbyEmail'],
    onCompleted: (data) => {
      if (!searchParams.has(STATE_SEARCH_PARAM)) {
        console.log('Missing Fullscript State Search Parameters');
      }

      const state: FullscriptAuthLinkState = JSON.parse(
        decodeURI(searchParams.get(STATE_SEARCH_PARAM) as string),
      ) as FullscriptAuthLinkState;

      if (state.patientId && data.linkProviderAccount?.success) {
        return onSuccess(state);
      }
      return onFailure(state);
    },
  });

  const { loading: providerDataLoading } = useGetFhirProviderByRequestIdQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const fullscriptAvailable =
        !!data?.getFHIRProviderByRequestId?.providers?.[0]?.fullscriptId;
      if (!fullscriptAvailable) {
        linkProvider;
      }
      handleContinue(VIRTUAL_PHARMACIES.fullscript);
    },
  });

  useEffect(() => {
    if (queryRun.current || !searchParams.has(CODE_SEARCH_PARAM)) return;
    const code = searchParams.get(CODE_SEARCH_PARAM) as string;
    queryRun.current = true;
    linkProvider({ variables: { tokenCode: code } });
  }, [linkProvider, searchParams]);

  const { data: locale, loading: loading } = useGetComponent({
    locale: 'en',
    componentId: componentIds.VIRTUAL_PHARMACY_MODAL,
  });

  const { data: genericAction, loading: genericActionLoading } =
    useGetGenericActions({
      locale: 'en',
      genericActionId: [genericActionsIds.CANCEL, genericActionsIds.CONTINUE],
    });

  const handleClose = async () => {
    await close();
    state.onModalClose && state.onModalClose();
  };

  const handleContinue = async (pharmacy?: string) => {
    await handleClose();
    if (!virtualPlatformSelect && !pharmacy) return;
    openPrescriptionModal(
      {
        platform: pharmacy ?? (virtualPlatformSelect as string),
        patientId: state.patientId,
        appointmentCodexId: state.appointmentId,
        encounterId: state.encounterId,
        onCloseCallback: state.onModalClose,
      },
      true,
    );
  };

  const handleVirtualPlatformSelect = (value: string) => {
    setVirtualPlatformSelect(value);
  };

  const Buttons: ModalButtonProps[] = [
    {
      label: genericAction?.[genericActionsIds.CONTINUE].continue,
      disabled: false,
      testID: 'continue',
      onClick: () => handleContinue(),
    },
    {
      label: genericAction?.[genericActionsIds.CANCEL].cancel,
      disabled: false,
      onClick: handleClose,
      testID: 'close',
      type: 'underline',
    },
  ];

  if (loading && !locale && genericActionLoading) return null;

  return (
    <Modal
      isOpen={isOpen}
      title={locale?.modalTitle}
      buttons={Buttons}
      onClose={handleClose}
      contentPaddingX="grow px-0 desktop:px-[30px]"
      contentPaddingY="py-0 desktop:py-[30px]"
      disableMobileGrow
      contentContainerDefaultBackground="bg-white"
      contentContainerDefaultBorder="border-b-2 border-base-content"
    >
      {providerDataLoading ? (
        <Loader />
      ) : (
        <div className="flex flex-col gap-5 desktop:gap-[60px] desktop:flex-row">
          <div className="flex flex-col py-2.5 desktop:max-h-[450px] desktop:flex-1">
            <div className="flex bg-base-content p-5 desktop:p-8 rounded-[10px] w-full">
              <div className="flex flex-col w-full">
                <p className="text-lg font-semibold text-charcoal-gray mb-3">
                  {locale?.chooseVirtualPlatform}
                  <span className="text-clc-red">*</span>
                </p>
                <div className="flex justify-between items-center w-full">
                  <InputComponent
                    type="radio"
                    isDisabled={true}
                    radioInputProps={{
                      radioLabelTextWeight: 'font-semibold',
                      radioError: null,
                      radioPosition: 'items-center desktop:items-start',
                      radioInputValue: VIRTUAL_PHARMACIES.dosespot,
                      radioInputLabel: 'DoseSpot for prescription medications',
                      radioInputCheckedValue:
                        virtualPlatformSelect === VIRTUAL_PHARMACIES.dosespot
                          ? VIRTUAL_PHARMACIES.dosespot
                          : undefined,
                      onRadioClick: () =>
                        handleVirtualPlatformSelect(
                          VIRTUAL_PHARMACIES.dosespot,
                        ),
                    }}
                  />
                  <InputComponent
                    type="radio"
                    radioInputProps={{
                      radioLabelTextWeight: 'font-semibold',
                      radioError: null,
                      radioPosition: 'items-center desktop:items-start',
                      radioInputValue: VIRTUAL_PHARMACIES.fullscript,
                      radioInputLabel: 'Fullscript for vitamins & supplements',
                      radioInputCheckedValue:
                        virtualPlatformSelect === VIRTUAL_PHARMACIES.fullscript
                          ? VIRTUAL_PHARMACIES.fullscript
                          : undefined,
                      onRadioClick: () =>
                        handleVirtualPlatformSelect(
                          VIRTUAL_PHARMACIES.fullscript,
                        ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
});
